/**
 * JWT Auth Client — manages Django-issued JWT tokens in the browser.
 *
 * Token storage:
 * - Access token: in-memory + sessionStorage (short-lived, 30 min)
 * - Refresh token: localStorage (long-lived, 14 days, rotated on each refresh)
 *
 * The API wrapper `apiFetch()` auto-injects Authorization headers and handles
 * transparent token refresh on 401. Tokens are only cleared (and the user
 * redirected to /login) when the refresh endpoint explicitly rejects the
 * refresh token — transient backend errors must not log the user out.
 */

import { env } from "@/config/env";
import { appEvents } from "@/shared/lib/events";

const ACCESS_TOKEN_KEY = "jwt_access";
const REFRESH_TOKEN_KEY = "jwt_refresh";

/** Prepend the appropriate base URL to relative paths. Absolute URLs pass through.
 *  /core/* routes go through coreUrl (same as apiUrl in production).
 *  All other paths go through apiUrl.
 */
export function resolveApiUrl(path: string): string {
  if (path.startsWith("http")) return path;
  if (path.startsWith("/core/")) return `${env.coreUrl}${path}`;
  return `${env.apiUrl}${path}`;
}

interface JwtPayload {
  user_id: string;
  email: string;
  role: string;
  name: string;
  exp: number;
  iat: number;
  token_type: "access" | "refresh";
}

interface AuthTokens {
  access: string;
  refresh: string;
}

export interface JwtUser {
  id: string;
  email: string;
  name: string;
  role: string;
}

interface LoginResponse {
  user: JwtUser;
  tokens: AuthTokens;
}

// ── Token storage ────────────────────────────────────────────────────────

let accessToken: string | null = null;
const PERMANENT_ENDPOINT_AUTH_FAILURE_PATHS = new Set([
  "/core/agent/speak",
  "/core/agent/voice/transcribe",
]);
const endpointAuthFailures = new Set<string>();

function getRefreshToken(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

function setTokens(tokens: AuthTokens): void {
  accessToken = tokens.access;
  endpointAuthFailures.clear();
  if (typeof window !== "undefined") {
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh);
    // Also store access token in sessionStorage for tab persistence
    sessionStorage.setItem(ACCESS_TOKEN_KEY, tokens.access);
  }
}

function notifyAuthStateChanged(user: JwtUser | null): void {
  appEvents.publish({ type: "auth:state-changed", user });
}

function clearTokens(): void {
  accessToken = null;
  endpointAuthFailures.clear();
  if (typeof window !== "undefined") {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  }
  notifyAuthStateChanged(null);
}

function restoreAccessToken(): string | null {
  if (accessToken) return accessToken;
  if (typeof window !== "undefined") {
    const stored = sessionStorage.getItem(ACCESS_TOKEN_KEY);
    if (stored && !isTokenExpired(stored)) {
      accessToken = stored;
      return stored;
    }
  }
  return null;
}

// ── JWT decode ───────────────────────────────────────────────────────────

function decodeJwt(token: string): JwtPayload | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
    return payload as JwtPayload;
  } catch {
    return null;
  }
}

function isTokenExpired(token: string, bufferSeconds = 30): boolean {
  const payload = decodeJwt(token);
  if (!payload?.exp) return true;
  return Date.now() / 1000 > payload.exp - bufferSeconds;
}

function authFailureCacheKey(url: RequestInfo | URL): string | null {
  const raw = typeof url === "string" ? url : url.toString();
  try {
    const base =
      typeof window !== "undefined" && window.location?.origin
        ? window.location.origin
        : "http://localhost";
    const parsed = new URL(raw, base);
    return PERMANENT_ENDPOINT_AUTH_FAILURE_PATHS.has(parsed.pathname) ? parsed.pathname : null;
  } catch {
    const path = raw.split("?")[0];
    return PERMANENT_ENDPOINT_AUTH_FAILURE_PATHS.has(path) ? path : null;
  }
}

// ── Token refresh ────────────────────────────────────────────────────────

let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  // Deduplicate concurrent refresh calls
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const refresh = getRefreshToken();
    if (!refresh) return null;

    try {
      const res = await fetch(resolveApiUrl("/auth/token/refresh/"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh }),
      });

      // Only treat explicit auth rejections as a dead session. Transient errors
      // (5xx, 429, network blips) must NOT wipe tokens — that would log the user
      // out on a single backend hiccup.
      if (res.status === 401 || res.status === 403) {
        console.error("[jwt-auth] refresh rejected, clearing tokens", { status: res.status });
        clearTokens();
        return null;
      }
      if (!res.ok) {
        console.error("[jwt-auth] refresh failed (transient), tokens kept", { status: res.status });
        return null;
      }

      const data = (await res.json()) as { access: string; refresh?: string };
      accessToken = data.access;
      if (typeof window !== "undefined") {
        sessionStorage.setItem(ACCESS_TOKEN_KEY, data.access);
        // Backend has ROTATE_REFRESH_TOKENS=True, so each call returns a fresh
        // refresh token. Persist it so the 14-day refresh window slides forward.
        if (data.refresh) {
          localStorage.setItem(REFRESH_TOKEN_KEY, data.refresh);
        }
      }
      notifyAuthStateChanged(getCurrentUser());
      return data.access;
    } catch (err) {
      console.error("[jwt-auth] refresh threw (network/parse), tokens kept", err);
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

// ── Public API ───────────────────────────────────────────────────────────

/**
 * Get the current access token, refreshing if expired.
 * Returns null if not authenticated.
 */
export async function getAccessToken(): Promise<string | null> {
  const token = restoreAccessToken();
  if (token && !isTokenExpired(token)) return token;
  return refreshAccessToken();
}

/**
 * Get user info from the current access token (synchronous, no refresh).
 */
export function getCurrentUser(): JwtUser | null {
  const token = restoreAccessToken();
  if (!token) return null;
  const payload = decodeJwt(token);
  if (!payload) return null;
  if (isTokenExpired(token)) return null;
  return {
    id: payload.user_id,
    email: payload.email,
    name: payload.name,
    role: payload.role,
  };
}

/**
 * Check if the user has a valid JWT session (may need refresh).
 */
export function hasJwtSession(): boolean {
  return restoreAccessToken() !== null || getRefreshToken() !== null;
}

/**
 * Login with email + password (Django JWT auth).
 */
export async function jwtLogin(email: string, password: string): Promise<LoginResponse> {
  const res = await fetch(resolveApiUrl("/auth/login/"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Login failed" }));
    throw new Error(err.error || "Login failed");
  }

  const data = (await res.json()) as LoginResponse;
  setTokens(data.tokens);
  notifyAuthStateChanged(data.user);
  return data;
}

/**
 * Register with email + password (Django JWT auth).
 */
export async function jwtRegister(
  email: string,
  password: string,
  name?: string,
): Promise<LoginResponse> {
  const res = await fetch(resolveApiUrl("/auth/register/"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password, name }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Registration failed" }));
    throw new Error(err.error || "Registration failed");
  }

  const data = (await res.json()) as LoginResponse;
  setTokens(data.tokens);
  notifyAuthStateChanged(data.user);
  return data;
}

/**
 * Google OAuth login (Django JWT auth) — One Tap / ID token.
 */
export async function jwtGoogleAuth(credential: string): Promise<LoginResponse> {
  const res = await fetch(resolveApiUrl("/auth/google/"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ credential }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Google auth failed" }));
    throw new Error(err.error || "Google auth failed");
  }

  const data = (await res.json()) as LoginResponse;
  setTokens(data.tokens);
  notifyAuthStateChanged(data.user);
  return data;
}

/**
 * Google OAuth authorization code flow — must use the same redirect_uri as in the auth request.
 */
export async function jwtGoogleAuthWithCode(
  code: string,
  redirectUri: string,
): Promise<LoginResponse> {
  const res = await fetch(resolveApiUrl("/auth/google/"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code, redirect_uri: redirectUri }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Google auth failed" }));
    throw new Error(err.error || "Google auth failed");
  }

  const data = (await res.json()) as LoginResponse;
  setTokens(data.tokens);
  notifyAuthStateChanged(data.user);
  return data;
}

/**
 * OTP: send a verification code to the given email.
 */
export async function jwtSendOtp(email: string): Promise<{ ok: boolean }> {
  const res = await fetch(resolveApiUrl("/auth/otp/send/"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Failed to send code" }));
    throw new Error(err.error || "Failed to send code");
  }

  return { ok: true };
}

/**
 * OTP: verify a code and get JWT tokens (auto-creates user if new).
 */
export async function jwtVerifyOtp(
  email: string,
  otp: string,
): Promise<LoginResponse & { created: boolean }> {
  const res = await fetch(resolveApiUrl("/auth/otp/verify/"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, otp }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Invalid code" }));
    throw new Error(err.error || "Invalid code");
  }

  const data = (await res.json()) as LoginResponse & { created: boolean };
  setTokens(data.tokens);
  notifyAuthStateChanged(data.user);
  return data;
}

/**
 * Update the current user's profile (requires valid JWT).
 */
export async function jwtUpdateProfile(fields: {
  name?: string;
  image?: string;
}): Promise<JwtUser> {
  const token = await getAccessToken();
  const res = await fetch(resolveApiUrl("/auth/me/update/"), {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(fields),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Update failed" }));
    throw new Error(err.error || "Update failed");
  }

  const data = (await res.json()) as JwtUser;
  notifyAuthStateChanged(data);
  return data;
}

/**
 * Keys that hold the user's UI preferences (theme, sidebar pinned state, etc.)
 * and must survive a logout. Everything else under `wiser*`, plus the named
 * zustand-persist stores, is wiped to prevent stale workspace/org/asset state
 * from one account leaking into the next.
 */
const PRESERVE_ON_LOGOUT = new Set([
  "wiserstudio-theme",
  "wiser_sidebar_pinned",
  "content-studio-view-mode",
]);

const APP_STATE_PREFIXES = ["wiser:", "wiser-", "wiser_", "briefs-in-flight:"];

const APP_STATE_EXACT_KEYS = [
  "coagent-store",
  "nova-store",
  "orchestrator-store",
  "saga-panel-state",
  "saga-action-plan",
];

function shouldClearStorageKey(key: string): boolean {
  if (PRESERVE_ON_LOGOUT.has(key)) return false;
  if (APP_STATE_EXACT_KEYS.includes(key)) return true;
  return APP_STATE_PREFIXES.some((p) => key.startsWith(p));
}

function clearAppState(): void {
  if (typeof window === "undefined") return;
  for (const storage of [window.localStorage, window.sessionStorage]) {
    const toRemove: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key && shouldClearStorageKey(key)) toRemove.push(key);
    }
    for (const key of toRemove) storage.removeItem(key);
  }
}

/**
 * Log out — clear JWT tokens and every cached workspace/org/asset key so the
 * next account doesn't inherit the previous user's state. UI preferences
 * (theme, sidebar pinned, content-studio view mode) are preserved.
 */
export function jwtLogout(): void {
  clearTokens();
  clearAppState();
}

/**
 * Fetch wrapper that auto-injects JWT Authorization header
 * and handles transparent token refresh on 401.
 */
export async function apiFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const token = await getAccessToken();
  const url = typeof input === "string" ? resolveApiUrl(input) : input;
  const endpointAuthFailureKey = authFailureCacheKey(url);
  if (endpointAuthFailureKey && endpointAuthFailures.has(endpointAuthFailureKey)) {
    return Response.json(
      { error: "Endpoint authorization failed" },
      { status: 401 },
    );
  }

  const headers = new Headers(init?.headers);
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  // Inject active organization context
  if (!headers.has("X-Organization-Id")) {
    try {
      const stored = localStorage.getItem("wiser-organization");
      if (stored) {
        const parsed = JSON.parse(stored);
        const wsId = parsed?.state?.activeOrganizationId;
        if (wsId) headers.set("X-Organization-Id", wsId);
      }
    } catch {
      // ignore parse errors
    }
  }

  let res = await fetch(url, { ...init, headers });

  // If 401 and we have a refresh token, try refreshing once
  let retriedAfterRefresh = false;
  if (res.status === 401 && getRefreshToken()) {
    console.error("[jwt-auth] 401 from", typeof input === "string" ? input : url, "— had token:", !!token, "— attempting refresh");
    const newToken = await refreshAccessToken();
    if (newToken) {
      headers.set("Authorization", `Bearer ${newToken}`);
      retriedAfterRefresh = true;
      res = await fetch(url, { ...init, headers });
      if (res.status === 401) {
        console.error("[jwt-auth] retry-after-refresh STILL 401 from", typeof input === "string" ? input : url);
        if (endpointAuthFailureKey) {
          endpointAuthFailures.add(endpointAuthFailureKey);
        }
      }
    }
  }

  if (res.status === 401 && retriedAfterRefresh && endpointAuthFailureKey) {
    endpointAuthFailures.add(endpointAuthFailureKey);
  }

  // Only boot to login when the session is genuinely dead — i.e. there is no
  // refresh token left (refreshAccessToken clears tokens only on 401/403 from
  // the refresh endpoint). A 401 with the refresh token still present means a
  // transient hiccup or a per-endpoint permission issue, not a logout.
  if (res.status === 401 && !getRefreshToken()) {
    console.error("[jwt-auth] 401 + no refresh token, redirecting to /login from", typeof input === "string" ? input : url);
    if (typeof window !== "undefined" && window.location && !window.location.pathname?.startsWith("/login")) {
      window.location.href = "/login";
    }
  }

  return res;
}
